import React from 'react';
import { Tooltip } from 'antd';
import {
  FctEstado, FctGlifoAtencion, FctIcono, nivelDesdeIncidencias,
  type FctFamilia, type FctNivelAtencion, type FctNombreIcono,
} from '../../fct/react';
import { INCIDENCIA_NIVEL, INCIDENCIA_TOOLTIPS, ESTADO_PENDIENTE, ESTADO_REALIZADO, ESTADO_DESCARTADO } from '../../api/reconciliacionApi';

/* ---------------------------------------------------------------------------
 * Capa semántica de Conciliación CU2 (GCVSC-I001 IT10, R-11/R-14):
 * las incidencias no tienen color propio (glifo + código en Mono + filete de fila);
 * los estados de operación van al semáforo con FctEstado.
 * ------------------------------------------------------------------------- */

/** Nivel de atención de un código de incidencia o bloqueo ([E1], [E4], …). */
export const nivelIncidencia = (codigo: string): FctNivelAtencion =>
  INCIDENCIA_NIVEL[codigo] ?? nivelDesdeIncidencias([codigo]);

/** Código de incidencia: glifo de atención + código en Mono, con el tooltip de siempre. */
export const IncidenciaCodigo: React.FC<{ codigo: string; tooltip?: React.ReactNode; nivel?: FctNivelAtencion }> = ({ codigo, tooltip, nivel }) => (
  <Tooltip title={tooltip ?? INCIDENCIA_TOOLTIPS[codigo]}>
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginRight: 4 }}>
      <FctGlifoAtencion nivel={nivel ?? nivelIncidencia(codigo)} />
      <span className="fct-codigo">{codigo}</span>
    </span>
  </Tooltip>
);

/** Estado de operación (Pendiente / Realizada / Descartada). Pendiente con E1/E2 activas → bloqueado. */
export const ESTADO_OPERACION: Record<number, { familia: FctFamilia; icono: FctNombreIcono }> = {
  [ESTADO_PENDIENTE]: { familia: 'aviso', icono: 'hora' },
  [ESTADO_REALIZADO]: { familia: 'exito', icono: 'completado' },
  [ESTADO_DESCARTADO]: { familia: 'neutro', icono: 'cancelado' },
};

export const EstadoOperacion: React.FC<{ estadoId: number; etiqueta: string; bloqueado?: boolean }> = ({ estadoId, etiqueta, bloqueado }) => {
  const base = ESTADO_OPERACION[estadoId] ?? { familia: 'neutro' as FctFamilia, icono: 'punto' as FctNombreIcono };
  const e = estadoId === ESTADO_PENDIENTE && bloqueado ? { familia: 'riesgo' as FctFamilia, icono: 'bloqueado' as FctNombreIcono } : base;
  return <FctEstado personalizado={{ etiqueta, familia: e.familia, icono: e.icono }} />;
};

/** Tipo de operación: alta (ALTA / AE) → éxito; baja (BAJA / CC / CE) → riesgo (decisión D.3). */
export const TipoOperacion: React.FC<{ alta: boolean; etiqueta: string }> = ({ alta, etiqueta }) => (
  <FctEstado personalizado={alta ? { etiqueta, familia: 'exito', icono: 'mas' } : { etiqueta, familia: 'riesgo', icono: 'cancelado' }} />
);

/** Icono de resultado del procesamiento en una tinta (sin color propio). */
export const ResultadoIcono: React.FC<{ ok: boolean; style?: React.CSSProperties }> = ({ ok, style }) => (
  <FctIcono nombre={ok ? 'completado' : 'cancelado'} titulo={ok ? 'Procesado' : 'No procesado'} style={{ width: 14, height: 14, ...style }} />
);
